/**
 * Create-node wizard for AGW / ACN (design §10.2, refined per VSNP-1093).
 *
 * The orchestrator only CREATES the record + a one-time token here; the actual
 * registration (node bootstrap + enrolment) is performed OUTSIDE the
 * orchestrator by an operator running the token. So step 1 collects only the
 * node's IDENTITY — name (→ node_id) and, for a connector, its group — plus the
 * token TTL. Placement, capacity, endpoints and upgrade policy live on the
 * node's Configuration tab (editable while awaiting enrolment); hardware and OS
 * are reported by the node itself at enrolment. Step 2 shows the token once.
 */

import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import Modal from '../common/Modal';
import AccessFormField from './AccessFormField';
import ClaimTokenPanel from './ClaimTokenPanel';
import { zodResolver } from '../../utils/zodResolver';
import accessService, { type MintedClaim } from '../../services/access.service';

const TTL_OPTIONS = [1, 3, 7, 14, 30] as const;

/**
 * Derive the node id (= certificate CN, the node's stable identity) from the
 * display name. Without an explicit nodeId the API generates an opaque UUID —
 * which the operator would then have to configure as the bootstrap's node id
 * without ever seeing it, since the enrolment CSR's CN must match the
 * registered node_id. A readable slug the token panel can show fixes that.
 * Matches the API's NODE_ID_PATTERN (3-64 of [A-Za-z0-9._-], letter/digit first).
 */
function nodeIdFromName(name: string): string {
  let slug = name
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^[^a-z0-9]+/, '')
    .replace(/-+$/, '')
    .slice(0, 64);
  if (slug.length < 3) slug = `${slug}${slug ? '-' : ''}node`.slice(0, 64);
  return slug;
}

// Sentinel option value that reveals the "new group name" input.
const NEW_GROUP = '__create__';
// Mirrors the API's connectorGroupNameSchema regex.
const GROUP_NAME_RE = /^[a-zA-Z0-9][a-zA-Z0-9._-]*$/;

const gatewaySchema = z
  .object({
    name: z.string().min(1, 'Name is required').max(255),
    // The LICENSE decision (VSNP-1126, D4): fixed at creation, forever.
    sharingMode: z.enum(['dedicated', 'shared']),
    ownerTenantId: z.string().optional(),
    maxTenants: z.coerce.number().int().min(2).max(100).optional(),
    expiresInDays: z.coerce.number().int().min(1).max(90),
  })
  .refine((v) => v.sharingMode !== 'dedicated' || !!v.ownerTenantId, {
    message: 'A dedicated gateway must name its owning tenant',
    path: ['ownerTenantId'],
  });

const connectorSchema = z
  .object({
    name: z.string().min(1, 'Name is required').max(255),
    connectorGroup: z.string().min(1, 'Select or create a connector group').max(128),
    newGroupName: z.string().max(128).optional(),
    expiresInDays: z.coerce.number().int().min(1).max(90),
  })
  .refine(
    (v) => v.connectorGroup !== NEW_GROUP || (!!v.newGroupName && GROUP_NAME_RE.test(v.newGroupName)),
    { message: 'Enter a valid group name (letters, digits, . _ - ; start with a letter or digit)', path: ['newGroupName'] },
  );

type GatewayForm = z.infer<typeof gatewaySchema>;
type ConnectorForm = z.infer<typeof connectorSchema>;

interface StepTwo {
  claim: MintedClaim;
  nodeName: string;
  nodeId: string;
}

interface RegisterNodeWizardProps {
  kind: 'gateway' | 'app_connector';
  isOpen: boolean;
  onClose: () => void;
  /** Called after the record exists (step 1 succeeded) so lists can refresh. */
  onRegistered: () => void;
  connectorGroups?: string[];
  /** Tenant choices for the gateway wizard's dedicated-owner picker (SuperAdmin). */
  tenants?: { id: string; name: string }[];
}

const RegisterNodeWizard = ({ kind, isOpen, onClose, onRegistered, connectorGroups = [], tenants = [] }: RegisterNodeWizardProps) => {
  const isGateway = kind === 'gateway';
  const [stepTwo, setStepTwo] = useState<StepTwo | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const gw = useForm<GatewayForm>({
    resolver: zodResolver<GatewayForm>(gatewaySchema),
    defaultValues: { name: '', sharingMode: 'dedicated', ownerTenantId: '', maxTenants: 10, expiresInDays: 7 },
  });
  const cn = useForm<ConnectorForm>({
    resolver: zodResolver<ConnectorForm>(connectorSchema),
    // No pre-selected group — the operator must pick an existing one or create a
    // new one by name. Groups are user-created, not seeded.
    defaultValues: { name: '', connectorGroup: '', newGroupName: '', expiresInDays: 7 },
  });

  const { reset: resetGw } = gw;
  const { reset: resetCn } = cn;
  useEffect(() => {
    if (isOpen) {
      resetGw();
      resetCn();
    }
  }, [isOpen, resetGw, resetCn]);

  // Step-two state is cleared on the close paths (event handlers), not in an
  // effect — this repo's lint bans setState-in-effect.
  const handleClose = () => {
    setStepTwo(null);
    setSubmitError(null);
    onClose();
  };

  const createGateway = gw.handleSubmit(async (values) => {
    setSubmitError(null);
    const nodeId = nodeIdFromName(values.name);
    await runCreate(values.name, nodeId, values.expiresInDays, () =>
      accessService.createGateway({
        name: values.name,
        nodeId,
        sharingMode: values.sharingMode,
        ownerTenantId: values.sharingMode === 'dedicated' ? values.ownerTenantId : undefined,
        maxTenants: values.sharingMode === 'shared' ? values.maxTenants : undefined,
      }),
    );
  });

  const createConnector = cn.handleSubmit(async (values) => {
    setSubmitError(null);
    const nodeId = nodeIdFromName(values.name);

    // Resolve the target group. "Create new" registers the group first so it
    // becomes a first-class, manageable group (a duplicate name is fine — it
    // already exists, so we just use it).
    let group = values.connectorGroup;
    if (group === NEW_GROUP) {
      group = (values.newGroupName ?? '').trim();
      try {
        await accessService.createConnectorGroup({ name: group });
      } catch (err) {
        const msg = err instanceof Error ? err.message : '';
        if (!/already exists/i.test(msg)) {
          setSubmitError(msg || 'Could not create the group');
          return;
        }
      }
    }

    await runCreate(values.name, nodeId, values.expiresInDays, () =>
      accessService.createConnector({ name: values.name, nodeId, connectorGroup: group }),
    );
  });

  const runCreate = async (
    name: string,
    nodeId: string,
    expiresInDays: number,
    create: () => Promise<{ id: string }>,
  ) => {
    let recordId: string;
    try {
      const record = await create();
      recordId = record.id;
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Could not create the record');
      return;
    }
    // The record now exists whatever happens next — tell the list.
    onRegistered();
    try {
      const claim = await accessService.mintNodeClaim(kind, recordId, expiresInDays);
      setStepTwo({ claim, nodeName: name, nodeId });
    } catch (err) {
      setSubmitError(
        `${name} was created, but minting its token failed: ` +
          `${err instanceof Error ? err.message : 'unknown error'}. ` +
          'Open the node page to generate a token.',
      );
    }
  };

  const noun = isGateway ? 'Access Gateway' : 'App Connector';
  const shortNoun = isGateway ? 'Gateway' : 'Connector';
  const submitting = isGateway ? gw.formState.isSubmitting : cn.formState.isSubmitting;
  const creatingNewGroup = useWatch({ control: cn.control, name: 'connectorGroup' }) === NEW_GROUP;
  const gatewayMode = useWatch({ control: gw.control, name: 'sharingMode' });

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={stepTwo ? `${noun} — install & enrol` : `Create ${noun}`}
      footer={
        stepTwo ? (
          <div className="flex justify-end">
            <button type="button" className="btn-primary" onClick={handleClose}>
              I've stored the token
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">Step 1 of 2 — identity</span>
            <div className="flex space-x-2">
              <button type="button" className="btn-secondary" onClick={handleClose} disabled={submitting}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={isGateway ? createGateway : createConnector}
                disabled={submitting}
              >
                {submitting ? 'Creating…' : `Create ${shortNoun}`}
              </button>
            </div>
          </div>
        )
      }
    >
      {stepTwo ? (
        <ClaimTokenPanel claim={stepTwo.claim} kind={kind} nodeName={stepTwo.nodeName} nodeId={stepTwo.nodeId} />
      ) : (
        <div className="space-y-4">
          {submitError && (
            <div className="rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
              {submitError}
            </div>
          )}

          <p className="text-sm text-gray-600">
            Only the node's identity is entered here. Placement, capacity, endpoints and upgrade policy
            are set on the node's page (editable while it awaits enrolment); hardware and OS are reported
            by the node itself when it enrols with the token from step 2.
          </p>

          {isGateway ? (
            <>
            <AccessFormField
              label="Name"
              htmlFor="wiz-gw-name"
              required
              error={gw.formState.errors.name?.message}
              hint="Identifies the gateway and becomes its node id (certificate CN)."
            >
              <input id="wiz-gw-name" className="input-base w-full" placeholder="syd-pop-1" {...gw.register('name')} />
            </AccessFormField>
            <AccessFormField
              label="License type"
              htmlFor="wiz-gw-mode"
              required
              error={gw.formState.errors.sharingMode?.message}
              hint="Permanent — a gateway cannot be converted later. Repurposing requires a factory reset and re-registration with a new token."
            >
              <div className="grid grid-cols-2 gap-3" id="wiz-gw-mode">
                <label className={`flex cursor-pointer flex-col rounded-md border px-3 py-2 ${gatewayMode === 'dedicated' ? 'border-primary-500 bg-primary-50/50' : 'border-gray-200'}`}>
                  <span className="flex items-center gap-2 text-sm font-medium text-gray-900">
                    <input type="radio" value="dedicated" {...gw.register('sharingMode')} />
                    Dedicated
                  </span>
                  <span className="mt-1 text-xs text-gray-500">One tenant owns the whole node. Strongest isolation.</span>
                </label>
                <label className={`flex cursor-pointer flex-col rounded-md border px-3 py-2 ${gatewayMode === 'shared' ? 'border-primary-500 bg-primary-50/50' : 'border-gray-200'}`}>
                  <span className="flex items-center gap-2 text-sm font-medium text-gray-900">
                    <input type="radio" value="shared" {...gw.register('sharingMode')} />
                    Shared
                  </span>
                  <span className="mt-1 text-xs text-gray-500">Multiple tenants, network-isolated. Assign tenants after creation.</span>
                </label>
              </div>
            </AccessFormField>
            {gatewayMode === 'dedicated' ? (
              <AccessFormField
                label="Owning tenant"
                htmlFor="wiz-gw-owner"
                required
                error={gw.formState.errors.ownerTenantId?.message}
                hint="This tenant is the gateway's only tenant, forever."
              >
                <select id="wiz-gw-owner" className="input-base w-full" {...gw.register('ownerTenantId')}>
                  <option value="" disabled>
                    Select a tenant…
                  </option>
                  {tenants.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </AccessFormField>
            ) : (
              <AccessFormField
                label="Max tenants"
                htmlFor="wiz-gw-maxten"
                error={gw.formState.errors.maxTenants?.message}
                hint="Capacity cap for tenant assignments on this shared gateway."
              >
                <input id="wiz-gw-maxten" type="number" min={2} max={100} className="input-base w-full" {...gw.register('maxTenants')} />
              </AccessFormField>
            )}
            </>
          ) : (
            <>
            <div className="grid grid-cols-2 gap-4">
              <AccessFormField
                label="Name"
                htmlFor="wiz-cn-name"
                required
                error={cn.formState.errors.name?.message}
                hint="Becomes the connector's node id (certificate CN)."
              >
                <input id="wiz-cn-name" className="input-base w-full" placeholder="syd-connector-1" {...cn.register('name')} />
              </AccessFormField>
              <AccessFormField
                label="Connector Group"
                htmlFor="wiz-cn-group"
                required
                error={cn.formState.errors.connectorGroup?.message}
                hint="Apps target a group, not a single connector."
              >
                <select id="wiz-cn-group" className="input-base w-full" {...cn.register('connectorGroup')}>
                  <option value="" disabled>
                    Select a group…
                  </option>
                  {connectorGroups.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                  <option value={NEW_GROUP}>+ Create new group…</option>
                </select>
              </AccessFormField>
            </div>
            {creatingNewGroup && (
              <AccessFormField
                label="New group name"
                htmlFor="wiz-cn-newgroup"
                required
                error={cn.formState.errors.newGroupName?.message}
                hint="Creates the group and assigns this connector to it."
              >
                <input
                  id="wiz-cn-newgroup"
                  className="input-base w-full"
                  placeholder="e.g. prod-web"
                  {...cn.register('newGroupName')}
                />
              </AccessFormField>
            )}
            </>
          )}

          <AccessFormField
            label="Token validity"
            htmlFor="wiz-ttl"
            hint="The one-time registration token expires if unused. It can be revoked or re-minted at any time."
          >
            <select id="wiz-ttl" className="input-base w-full" {...(isGateway ? gw.register('expiresInDays') : cn.register('expiresInDays'))}>
              {TTL_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {d} day{d === 1 ? '' : 's'}
                </option>
              ))}
            </select>
          </AccessFormField>
        </div>
      )}
    </Modal>
  );
};

export default RegisterNodeWizard;
